'use client';

import { useMemo, useCallback, useState, type ChangeEvent } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { SUPPORTED_COUNTRIES, getDefaultCountry } from '@/lib/config-system/countries';
import { normalizePhoneToE164 } from '@/lib/phone/normalize';
import { nationalDigits } from './phone-format';

export interface CountryCodeOption {
  code: string;
  dial: string;
  label: string;
  flag: string;
}

/**
 * The picker list is the supported-country registry (`configs/countries/
 * supported.json`), in registry order. Do not inline dial codes here — the
 * registry is also the server's source of truth for the `country` attribute.
 */
export const COUNTRY_CODES: CountryCodeOption[] = SUPPORTED_COUNTRIES.map((country) => ({
  code: country.iso2,
  dial: country.dial,
  label: country.name,
  flag: country.flag,
}));

const DEFAULT_COUNTRY = getDefaultCountry();
const DEFAULT_DIAL_CODE = DEFAULT_COUNTRY?.dial ?? '233';
const DEFAULT_ISO2 = DEFAULT_COUNTRY?.iso2 ?? 'GH';

export { formatPhoneWithCountry } from './phone-format';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * ISO2 region the national part is read in (`GH`). A dial code (`233`) is
   * still accepted for older callers. Passing the ISO2 into the dial slot is
   * what used to render a `+GH` prefix and compose `+GH201…`, which the server
   * rejected as an invalid number.
   */
  countryCode?: string;
  onCountryCodeChange?: (code: string) => void;
  placeholder?: string;
  label?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
}

export function PhoneInput({
  value: fullValue,
  onChange,
  countryCode: countryCodeProp,
  onCountryCodeChange,
  placeholder = '+233...',
  id,
  className,
  disabled,
}: PhoneInputProps) {
  // `countryCodeProp` is an ISO2 ("GH") from the auth modal, not a dial code.
  // Resolve it through the registry so the prefix reads "+233" and composed
  // values stay E.164. Dial-code callers keep working via the second lookup.
  const propCountry = useMemo(() => {
    const raw = (countryCodeProp ?? '').trim();
    if (!raw) return null;
    return (
      COUNTRY_CODES.find((cc) => cc.code.toUpperCase() === raw.toUpperCase()) ??
      COUNTRY_CODES.find((cc) => cc.dial === raw) ??
      null
    );
  }, [countryCodeProp]);
  const propDialCode = propCountry?.dial ?? DEFAULT_DIAL_CODE;
  const propIso2 = propCountry?.code ?? DEFAULT_ISO2;

  const { dialCode, phonePart } = useMemo(() => {
    const cleaned = fullValue.replace(/[^0-9+]/g, '');
    if (cleaned.startsWith('+')) {
      const match = COUNTRY_CODES.find((cc) => cleaned.startsWith(`+${cc.dial}`));
      if (match) {
        return {
          dialCode: match.dial,
          phonePart: cleaned.slice(match.dial.length + 1),
        };
      }
    }
    return { dialCode: propDialCode, phonePart: nationalDigits(fullValue) };
  }, [fullValue, propDialCode]);

  const selectedCountry = COUNTRY_CODES.find((cc) => cc.dial === dialCode);
  const selectedIso2 = selectedCountry?.code || propIso2;
  const [open, setOpen] = useState(false);

  const handlePhoneChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const typed = e.target.value;
      // An international form pasted into the national field (+233… / 00233…)
      // used to have the dial prepended again — "+233233…" — so the SMS went to
      // a number that did not exist and the code never verified. Read the
      // country from the number itself and keep only the national digits.
      if (typed.startsWith('+') || typed.startsWith('00')) {
        const result = normalizePhoneToE164(typed, selectedIso2);
        if (result.ok) {
          const iso2 = result.country?.iso2;
          if (iso2 && iso2 !== selectedIso2) onCountryCodeChange?.(iso2);
          const dial = result.country?.dial ?? dialCode;
          onChange(`+${dial}${result.national}`);
          return;
        }
        // Incomplete international entry — strip any leading dial so it is not
        // applied twice while the customer keeps typing.
        const stripped = typed
          .replace(/[^0-9]/g, '')
          .replace(/^00/, '')
          .replace(new RegExp(`^${dialCode}`), '');
        onChange(stripped ? `+${dialCode}${stripped}` : '');
        return;
      }
      // A bare number that carries the selected dial code (`233201234567`)
      // is the other way customers paste an international number. Strip the
      // redundant dial instead of nesting it (`+233233…`).
      const digitsOnly = typed.replace(/\D/g, '');
      const carriesDial =
        digitsOnly.startsWith(dialCode) &&
        digitsOnly.length - dialCode.length >= 7;
      const raw = nationalDigits(carriesDial ? digitsOnly.slice(dialCode.length) : typed);
      if (raw === phonePart) return;
      onChange(raw ? `+${dialCode}${raw}` : '');
    },
    [dialCode, phonePart, selectedIso2, onChange, onCountryCodeChange],
  );

  const handleCountryChange = useCallback(
    (newCode: string) => {
      if (onCountryCodeChange) {
        onCountryCodeChange(newCode);
      }
      const country = COUNTRY_CODES.find((cc) => cc.code === newCode);
      // phonePart can arrive with a trunk `0` when `value` was prefilled from
      // outside this component, so re-strip rather than trusting it.
      const national = nationalDigits(phonePart);
      if (country && national) {
        onChange(`+${country.dial}${national}`);
      }
    },
    [phonePart, onChange, onCountryCodeChange],
  );

  return (
    <div className={className}>
      <div className="flex gap-2">
        {/* Searchable country picker. A plain Select hid Ghana behind a long
            unsearchable list (and mis-detected the market), so the customer
            could not find the right dial code. Popover + Command lets them type
            "Ghana", "233" or "GH"; z-[1300] keeps it above the auth dialog. */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Select country code"
              className="flex h-10 w-[124px] shrink-0 items-center justify-between gap-1 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-[border-color,outline-color] focus-visible:border-ring focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {selectedCountry ? (
                  <>
                    <span>{selectedCountry.flag}</span>
                    <span className="truncate">+{selectedCountry.dial}</span>
                  </>
                ) : (
                  <span>+{dialCode}</span>
                )}
              </span>
              <ChevronDown className="size-3.5 shrink-0 opacity-50" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="z-[1300] w-[260px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search country or code" />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {COUNTRY_CODES.map((cc) => (
                    <CommandItem
                      key={cc.code}
                      value={`${cc.label} ${cc.code} ${cc.dial}`}
                      onSelect={() => {
                        handleCountryChange(cc.code);
                        setOpen(false);
                      }}
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <span>{cc.flag}</span>
                        <span className="truncate">{cc.label}</span>
                        <span className="text-muted-foreground">+{cc.dial}</span>
                      </span>
                      {cc.code === selectedIso2 && <Check className="size-4 shrink-0" aria-hidden="true" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          id={id}
          type="tel"
          inputMode="tel"
          value={phonePart}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className="flex-1"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
